import React, { useRef, useEffect, useState, ReactNode } from 'react';
import './CustomScrollbar.css';

interface CustomScrollbarProps {
  children: ReactNode;
  className?: string;
  trackRight?: number | string;
  trackTop?: number | string;
  trackClassName?: string;
  thumbClassName?: string;
}

const CustomScrollbar: React.FC<CustomScrollbarProps> = ({ 
  children, 
  className = '',
  trackRight = 5,
  trackTop = 0,
  trackClassName = '',
  thumbClassName = ''
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollThumbHeight, setScrollThumbHeight] = useState(0);
  const [trackHeight, setTrackHeight] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragStartScrollTop = useRef(0);

  // Вычисление размеров скроллбара
  useEffect(() => {
    const updateScrollbar = () => {
      const content = contentRef.current;
      if (!content) return;

      const scrollHeight = content.scrollHeight;
      const clientHeight = content.clientHeight;
      const maxScroll = scrollHeight - clientHeight;

      if (maxScroll <= 0) {
        setScrollThumbHeight(0);
        setScrollPosition(0);
        setTrackHeight(0);
        return;
      }

      // Высота ползунка пропорциональна видимой области
      const thumbHeight = (clientHeight / scrollHeight) * clientHeight;
      const finalThumbHeight = Math.max(thumbHeight, 20); // Минимум 20px
      setScrollThumbHeight(finalThumbHeight);

      // Позиция ползунка
      const currentTrackHeight = clientHeight;
      setTrackHeight(currentTrackHeight);
      const scrollableTrackHeight = currentTrackHeight - finalThumbHeight;
      const scrollRatio = content.scrollTop / maxScroll;
      const thumbTop = scrollRatio * scrollableTrackHeight;
      setScrollPosition(Math.max(0, Math.min(scrollableTrackHeight, thumbTop)));
    };

    updateScrollbar();
    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', updateScrollbar);
      window.addEventListener('resize', updateScrollbar);
      const resizeObserver = new ResizeObserver(updateScrollbar);
      resizeObserver.observe(content);
      
      return () => {
        content.removeEventListener('scroll', updateScrollbar);
        window.removeEventListener('resize', updateScrollbar);
        resizeObserver.disconnect();
      };
    }
  }, [children]);

  // Обработка перетаскивания ползунка
  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartY.current = e.clientY;
    if (contentRef.current) {
      dragStartScrollTop.current = contentRef.current.scrollTop;
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !contentRef.current || !thumbRef.current) return;

      const content = contentRef.current;
      const scrollHeight = content.scrollHeight;
      const clientHeight = content.clientHeight;
      const maxScroll = scrollHeight - clientHeight;

      if (maxScroll <= 0) return;

      const deltaY = e.clientY - dragStartY.current;
      const scrollbarHeight = clientHeight;
      const thumbHeight = scrollThumbHeight;
      const scrollableHeight = scrollbarHeight - thumbHeight;

      const scrollDelta = (deltaY / scrollableHeight) * maxScroll;
      const newScrollTop = Math.max(0, Math.min(maxScroll, dragStartScrollTop.current + scrollDelta));
      
      content.scrollTop = newScrollTop;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, scrollThumbHeight]);

  // Обработка клика на дорожку скроллбара
  const handleTrackClick = (e: React.MouseEvent) => {
    if (!contentRef.current || !scrollbarRef.current) return;
    
    const track = scrollbarRef.current;
    const trackRect = track.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;
    const trackHeight = trackRect.height;
    const thumbHeight = scrollThumbHeight;
    
    const content = contentRef.current;
    const scrollHeight = content.scrollHeight;
    const clientHeight = content.clientHeight;
    const maxScroll = scrollHeight - clientHeight;

    if (maxScroll <= 0) return;

    const scrollableHeight = trackHeight - thumbHeight;
    const scrollRatio = clickY / scrollableHeight;
    const newScrollTop = scrollRatio * maxScroll;

    content.scrollTop = Math.max(0, Math.min(maxScroll, newScrollTop));
  };

  return (
    <div className={`custom-scrollbar-wrapper ${className}`}>
      <div 
        className="custom-scrollbar-content"
        ref={contentRef}
      >
        {children}
      </div>
      {scrollThumbHeight > 0 && trackHeight > 0 && (
        <div 
          className={`custom-scrollbar-track ${trackClassName}`}
          ref={scrollbarRef}
          onClick={handleTrackClick}
          style={{
            height: `${trackHeight}px`,
            right: typeof trackRight === 'number' ? `${trackRight}px` : trackRight,
            top: typeof trackTop === 'number' ? `${trackTop}px` : trackTop,
          }}
        >
          <div
            className={`custom-scrollbar-thumb ${thumbClassName}`}
            ref={thumbRef}
            style={{
              height: `${scrollThumbHeight}px`,
              top: `${scrollPosition}px`,
            }}
            onMouseDown={handleThumbMouseDown}
          />
        </div>
      )}
    </div>
  );
};

export default CustomScrollbar;
